/**
 * Saved Searches Seed Data
 *
 * Defines seed data for saved/favorite searches that users can quickly access.
 * Saved searches store filter criteria, sort orders, and column configurations.
 *
 * NOTE: The SavedSearch model does not yet exist in any service schema.
 * This seed data is prepared for when the model is implemented.
 * Recommended service: Case Management (29) or Entity Framework (30)
 *
 * Use cases:
 * - Case pipeline views (e.g., "My Active Cases", "Overdue Cases")
 * - Consumer segments (e.g., "High Net Worth", "Recent Submissions")
 * - Partner lists (e.g., "Top Performers", "Pending Onboarding")
 * - Task filters (e.g., "My Urgent Tasks", "Team Blockers")
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// ============================================================================
// IDs
// ============================================================================

export const SAVED_SEARCH_IDS = {
  // Case Pipeline Searches
  MY_ACTIVE_CASES: seedId('saved-search-my-active', 1),
  OVERDUE_CASES: seedId('saved-search-overdue', 2),
  UNDERWRITING_QUEUE: seedId('saved-search-uw-queue', 3),
  CLOSING_THIS_WEEK: seedId('saved-search-closing-week', 4),
  HIGH_VALUE_DEALS: seedId('saved-search-high-value', 5),

  // Consumer Searches
  HIGH_NET_WORTH: seedId('saved-search-hnw', 6),
  RECENT_SUBMISSIONS: seedId('saved-search-recent-sub', 7),
  REPEAT_BORROWERS: seedId('saved-search-repeat', 8),

  // Partner Searches
  TOP_PERFORMERS: seedId('saved-search-top-partners', 9),
  PENDING_ONBOARDING: seedId('saved-search-pending-partner', 10),
  INACTIVE_PARTNERS: seedId('saved-search-inactive-partner', 11),

  // Task Searches
  MY_URGENT_TASKS: seedId('saved-search-urgent-tasks', 12),
  TEAM_BLOCKERS: seedId('saved-search-blockers', 13),
  OVERDUE_TASKS: seedId('saved-search-overdue-tasks', 14),

  // 1Sharpe Capital searches
  SHARPE_PIPELINE: seedId('saved-search-sharpe-pipeline', 15),
  SHARPE_BRIDGE_LOANS: seedId('saved-search-sharpe-bridge', 16),
} as const;

// ============================================================================
// Types
// ============================================================================

export type SavedSearchEntity = 'case' | 'consumer' | 'partner' | 'task' | 'lead' | 'referral';

export type SavedSearchVisibility = 'private' | 'team' | 'business';

export interface FilterCriteria {
  field: string;
  operator:
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'starts_with'
    | 'ends_with'
    | 'greater_than'
    | 'less_than'
    | 'between'
    | 'in'
    | 'not_in'
    | 'is_null'
    | 'is_not_null';
  value: string | number | boolean | string[] | number[] | null;
  secondValue?: string | number; // For 'between' operator
}

export interface SortCriteria {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ColumnConfig {
  field: string;
  width?: number;
  visible: boolean;
  order: number;
}

export interface SavedSearchSeedData {
  id: string;
  businessId: string;
  userId: string; // Owner of the search
  teamId: string | null; // If shared with team

  // Search metadata
  name: string;
  description: string | null;
  entity: SavedSearchEntity;
  visibility: SavedSearchVisibility;
  icon: string | null;
  color: string | null;

  // Search configuration
  filters: FilterCriteria[];
  sorts: SortCriteria[];
  columns: ColumnConfig[] | null; // null = use default columns

  // Usage tracking
  usageCount: number;
  lastUsedAt: Date | null;
  isPinned: boolean; // Show in quick access
  isDefault: boolean; // Default view for entity

  // Audit
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Seed Data
// ============================================================================

const SAVED_SEARCHES: SavedSearchSeedData[] = [
  // ============================================================================
  // Case Pipeline Searches (Rachel Martinez - OpsMatrix)
  // ============================================================================
  {
    id: SAVED_SEARCH_IDS.MY_ACTIVE_CASES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'My Active Cases',
    description: 'All cases assigned to me that are currently active',
    entity: 'case',
    visibility: 'private',
    icon: 'folder_open',
    color: '#4CAF50',
    filters: [
      {
        field: 'assigneeId',
        operator: 'equals',
        value: '{{currentUserId}}', // Dynamic placeholder
      },
      {
        field: 'status',
        operator: 'in',
        value: ['OPEN', 'IN_PROGRESS', 'PENDING'],
      },
    ],
    sorts: [{ field: 'updatedAt', direction: 'desc' }],
    columns: [
      { field: 'caseNumber', visible: true, order: 1 },
      { field: 'consumerName', visible: true, order: 2 },
      { field: 'caseType', visible: true, order: 3 },
      { field: 'status', visible: true, order: 4 },
      { field: 'priority', visible: true, order: 5 },
      { field: 'loanAmount', visible: true, order: 6 },
      { field: 'updatedAt', visible: true, order: 7 },
    ],
    usageCount: 234,
    lastUsedAt: new Date('2026-01-28'),
    isPinned: true,
    isDefault: true,
    createdAt: new Date('2025-06-01'),
    updatedAt: new Date('2026-01-15'),
  },
  {
    id: SAVED_SEARCH_IDS.OVERDUE_CASES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'Overdue Cases',
    description: 'Cases past their SLA or target close date',
    entity: 'case',
    visibility: 'team',
    icon: 'warning',
    color: '#F44336',
    filters: [
      {
        field: 'status',
        operator: 'not_in',
        value: ['CLOSED', 'CANCELLED', 'WITHDRAWN'],
      },
      {
        field: 'targetCloseDate',
        operator: 'less_than',
        value: '{{today}}',
      },
    ],
    sorts: [
      { field: 'targetCloseDate', direction: 'asc' },
      { field: 'priority', direction: 'desc' },
    ],
    columns: [
      { field: 'caseNumber', visible: true, order: 1 },
      { field: 'consumerName', visible: true, order: 2 },
      { field: 'assigneeName', visible: true, order: 3 },
      { field: 'targetCloseDate', visible: true, order: 4 },
      { field: 'daysOverdue', visible: true, order: 5 },
      { field: 'priority', visible: true, order: 6 },
    ],
    usageCount: 89,
    lastUsedAt: new Date('2026-01-27'),
    isPinned: true,
    isDefault: false,
    createdAt: new Date('2025-07-15'),
    updatedAt: new Date('2026-01-20'),
  },
  {
    id: SAVED_SEARCH_IDS.UNDERWRITING_QUEUE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.MIKE_CHEN,
    teamId: null,
    name: 'Underwriting Queue',
    description: 'Cases ready for underwriting review',
    entity: 'case',
    visibility: 'team',
    icon: 'rate_review',
    color: '#2196F3',
    filters: [
      {
        field: 'pipelineStage',
        operator: 'equals',
        value: 'UNDERWRITING',
      },
      {
        field: 'status',
        operator: 'equals',
        value: 'IN_PROGRESS',
      },
    ],
    sorts: [
      { field: 'priority', direction: 'desc' },
      { field: 'submittedAt', direction: 'asc' },
    ],
    columns: null, // Use default columns
    usageCount: 156,
    lastUsedAt: new Date('2026-01-28'),
    isPinned: true,
    isDefault: false,
    createdAt: new Date('2025-08-01'),
    updatedAt: new Date('2026-01-22'),
  },
  {
    id: SAVED_SEARCH_IDS.CLOSING_THIS_WEEK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'Closing This Week',
    description: 'Cases scheduled to close within the next 7 days',
    entity: 'case',
    visibility: 'business',
    icon: 'event_available',
    color: '#9C27B0',
    filters: [
      {
        field: 'pipelineStage',
        operator: 'equals',
        value: 'CLOSING',
      },
      {
        field: 'targetCloseDate',
        operator: 'between',
        value: '{{today}}',
        secondValue: '{{today+7}}',
      },
    ],
    sorts: [{ field: 'targetCloseDate', direction: 'asc' }],
    columns: [
      { field: 'caseNumber', visible: true, order: 1 },
      { field: 'consumerName', visible: true, order: 2 },
      { field: 'loanAmount', visible: true, order: 3 },
      { field: 'targetCloseDate', visible: true, order: 4 },
      { field: 'assigneeName', visible: true, order: 5 },
      { field: 'closingAgent', visible: true, order: 6 },
    ],
    usageCount: 78,
    lastUsedAt: new Date('2026-01-28'),
    isPinned: false,
    isDefault: false,
    createdAt: new Date('2025-09-01'),
    updatedAt: new Date('2026-01-10'),
  },
  {
    id: SAVED_SEARCH_IDS.HIGH_VALUE_DEALS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'High Value Deals ($1M+)',
    description: 'Cases with loan amounts over $1 million',
    entity: 'case',
    visibility: 'business',
    icon: 'attach_money',
    color: '#FF9800',
    filters: [
      {
        field: 'loanAmount',
        operator: 'greater_than',
        value: 1000000,
      },
      {
        field: 'status',
        operator: 'not_in',
        value: ['CLOSED', 'CANCELLED'],
      },
    ],
    sorts: [
      { field: 'loanAmount', direction: 'desc' },
      { field: 'priority', direction: 'desc' },
    ],
    columns: null,
    usageCount: 45,
    lastUsedAt: new Date('2026-01-25'),
    isPinned: false,
    isDefault: false,
    createdAt: new Date('2025-10-01'),
    updatedAt: new Date('2026-01-05'),
  },

  // ============================================================================
  // Consumer Searches
  // ============================================================================
  {
    id: SAVED_SEARCH_IDS.HIGH_NET_WORTH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'High Net Worth Consumers',
    description: 'Consumers with total deal volume over $5M',
    entity: 'consumer',
    visibility: 'team',
    icon: 'diamond',
    color: '#673AB7',
    filters: [
      {
        field: 'totalDealVolume',
        operator: 'greater_than',
        value: 5000000,
      },
      {
        field: 'status',
        operator: 'equals',
        value: 'ACTIVE',
      },
    ],
    sorts: [{ field: 'totalDealVolume', direction: 'desc' }],
    columns: null,
    usageCount: 34,
    lastUsedAt: new Date('2026-01-20'),
    isPinned: false,
    isDefault: false,
    createdAt: new Date('2025-11-01'),
    updatedAt: new Date('2026-01-15'),
  },
  {
    id: SAVED_SEARCH_IDS.RECENT_SUBMISSIONS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.MIKE_CHEN,
    teamId: null,
    name: 'Recent Submissions (30 days)',
    description: 'New consumer applications in the last 30 days',
    entity: 'consumer',
    visibility: 'business',
    icon: 'fiber_new',
    color: '#00BCD4',
    filters: [
      {
        field: 'createdAt',
        operator: 'greater_than',
        value: '{{today-30}}',
      },
    ],
    sorts: [{ field: 'createdAt', direction: 'desc' }],
    columns: null,
    usageCount: 123,
    lastUsedAt: new Date('2026-01-28'),
    isPinned: true,
    isDefault: false,
    createdAt: new Date('2025-08-15'),
    updatedAt: new Date('2026-01-20'),
  },
  {
    id: SAVED_SEARCH_IDS.REPEAT_BORROWERS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'Repeat Borrowers',
    description: 'Consumers with 2+ completed deals',
    entity: 'consumer',
    visibility: 'team',
    icon: 'repeat',
    color: '#8BC34A',
    filters: [
      {
        field: 'completedDealsCount',
        operator: 'greater_than',
        value: 1,
      },
    ],
    sorts: [{ field: 'completedDealsCount', direction: 'desc' }],
    columns: null,
    usageCount: 28,
    lastUsedAt: new Date('2026-01-18'),
    isPinned: false,
    isDefault: false,
    createdAt: new Date('2025-12-01'),
    updatedAt: new Date('2026-01-10'),
  },

  // ============================================================================
  // Partner Searches
  // ============================================================================
  {
    id: SAVED_SEARCH_IDS.TOP_PERFORMERS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'Top Performing Partners',
    description: 'Partners with highest referral conversion rates',
    entity: 'partner',
    visibility: 'business',
    icon: 'star',
    color: '#FFC107',
    filters: [
      {
        field: 'status',
        operator: 'equals',
        value: 'ACTIVE',
      },
      {
        field: 'conversionRate',
        operator: 'greater_than',
        value: 0.5, // 50%+
      },
    ],
    sorts: [
      { field: 'conversionRate', direction: 'desc' },
      { field: 'totalReferrals', direction: 'desc' },
    ],
    columns: null,
    usageCount: 67,
    lastUsedAt: new Date('2026-01-25'),
    isPinned: true,
    isDefault: false,
    createdAt: new Date('2025-07-01'),
    updatedAt: new Date('2026-01-20'),
  },
  {
    id: SAVED_SEARCH_IDS.PENDING_ONBOARDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'Pending Onboarding',
    description: 'Partners awaiting approval or document completion',
    entity: 'partner',
    visibility: 'team',
    icon: 'pending_actions',
    color: '#FF5722',
    filters: [
      {
        field: 'status',
        operator: 'in',
        value: ['PENDING', 'DOCUMENTS_REQUIRED', 'UNDER_REVIEW'],
      },
    ],
    sorts: [{ field: 'createdAt', direction: 'asc' }],
    columns: null,
    usageCount: 45,
    lastUsedAt: new Date('2026-01-27'),
    isPinned: false,
    isDefault: false,
    createdAt: new Date('2025-09-01'),
    updatedAt: new Date('2026-01-15'),
  },
  {
    id: SAVED_SEARCH_IDS.INACTIVE_PARTNERS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'Inactive Partners (90+ days)',
    description: 'Active partners with no referrals in 90 days',
    entity: 'partner',
    visibility: 'team',
    icon: 'hourglass_empty',
    color: '#9E9E9E',
    filters: [
      {
        field: 'status',
        operator: 'equals',
        value: 'ACTIVE',
      },
      {
        field: 'lastReferralAt',
        operator: 'less_than',
        value: '{{today-90}}',
      },
    ],
    sorts: [{ field: 'lastReferralAt', direction: 'asc' }],
    columns: null,
    usageCount: 12,
    lastUsedAt: new Date('2026-01-15'),
    isPinned: false,
    isDefault: false,
    createdAt: new Date('2025-11-15'),
    updatedAt: new Date('2026-01-10'),
  },

  // ============================================================================
  // Task Searches
  // ============================================================================
  {
    id: SAVED_SEARCH_IDS.MY_URGENT_TASKS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'My Urgent Tasks',
    description: 'High priority tasks assigned to me',
    entity: 'task',
    visibility: 'private',
    icon: 'priority_high',
    color: '#F44336',
    filters: [
      {
        field: 'assigneeId',
        operator: 'equals',
        value: '{{currentUserId}}',
      },
      {
        field: 'priority',
        operator: 'in',
        value: ['URGENT', 'HIGH'],
      },
      {
        field: 'status',
        operator: 'not_in',
        value: ['COMPLETED', 'CANCELLED'],
      },
    ],
    sorts: [
      { field: 'priority', direction: 'desc' },
      { field: 'dueDate', direction: 'asc' },
    ],
    columns: null,
    usageCount: 189,
    lastUsedAt: new Date('2026-01-28'),
    isPinned: true,
    isDefault: true,
    createdAt: new Date('2025-06-15'),
    updatedAt: new Date('2026-01-25'),
  },
  {
    id: SAVED_SEARCH_IDS.TEAM_BLOCKERS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.MIKE_CHEN,
    teamId: null,
    name: 'Team Blockers',
    description: 'Tasks marked as blocking other work',
    entity: 'task',
    visibility: 'team',
    icon: 'block',
    color: '#E91E63',
    filters: [
      {
        field: 'isBlocking',
        operator: 'equals',
        value: true,
      },
      {
        field: 'status',
        operator: 'not_in',
        value: ['COMPLETED', 'CANCELLED'],
      },
    ],
    sorts: [
      { field: 'priority', direction: 'desc' },
      { field: 'createdAt', direction: 'asc' },
    ],
    columns: null,
    usageCount: 56,
    lastUsedAt: new Date('2026-01-26'),
    isPinned: true,
    isDefault: false,
    createdAt: new Date('2025-10-01'),
    updatedAt: new Date('2026-01-20'),
  },
  {
    id: SAVED_SEARCH_IDS.OVERDUE_TASKS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    teamId: null,
    name: 'Overdue Tasks',
    description: 'Tasks past their due date',
    entity: 'task',
    visibility: 'team',
    icon: 'schedule',
    color: '#FF9800',
    filters: [
      {
        field: 'dueDate',
        operator: 'less_than',
        value: '{{today}}',
      },
      {
        field: 'status',
        operator: 'not_in',
        value: ['COMPLETED', 'CANCELLED'],
      },
    ],
    sorts: [{ field: 'dueDate', direction: 'asc' }],
    columns: null,
    usageCount: 98,
    lastUsedAt: new Date('2026-01-28'),
    isPinned: false,
    isDefault: false,
    createdAt: new Date('2025-08-01'),
    updatedAt: new Date('2026-01-22'),
  },

  // ============================================================================
  // 1Sharpe Capital Searches
  // ============================================================================
  {
    id: SAVED_SEARCH_IDS.SHARPE_PIPELINE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    userId: USER_IDS.SHARPE_ADMIN,
    teamId: null,
    name: '1Sharpe Full Pipeline',
    description: 'All active cases in the 1Sharpe pipeline',
    entity: 'case',
    visibility: 'business',
    icon: 'view_timeline',
    color: '#3F51B5',
    filters: [
      {
        field: 'status',
        operator: 'not_in',
        value: ['CLOSED', 'CANCELLED', 'WITHDRAWN'],
      },
    ],
    sorts: [
      { field: 'pipelineStage', direction: 'asc' },
      { field: 'priority', direction: 'desc' },
    ],
    columns: null,
    usageCount: 145,
    lastUsedAt: new Date('2026-01-28'),
    isPinned: true,
    isDefault: true,
    createdAt: new Date('2025-07-01'),
    updatedAt: new Date('2026-01-25'),
  },
  {
    id: SAVED_SEARCH_IDS.SHARPE_BRIDGE_LOANS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    userId: USER_IDS.SHARPE_ADMIN,
    teamId: null,
    name: 'Bridge Loans Only',
    description: 'Filter to show only bridge loan cases',
    entity: 'case',
    visibility: 'business',
    icon: 'swap_horiz',
    color: '#009688',
    filters: [
      {
        field: 'loanType',
        operator: 'equals',
        value: 'BRIDGE',
      },
      {
        field: 'status',
        operator: 'not_in',
        value: ['CLOSED', 'CANCELLED'],
      },
    ],
    sorts: [
      { field: 'loanAmount', direction: 'desc' },
      { field: 'submittedAt', direction: 'asc' },
    ],
    columns: null,
    usageCount: 78,
    lastUsedAt: new Date('2026-01-27'),
    isPinned: false,
    isDefault: false,
    createdAt: new Date('2025-09-15'),
    updatedAt: new Date('2026-01-20'),
  },
];

// ============================================================================
// Getter Functions
// ============================================================================

export function getAllSavedSearches(): SavedSearchSeedData[] {
  return SAVED_SEARCHES;
}

export function getSavedSearchesByBusiness(businessId: string): SavedSearchSeedData[] {
  return SAVED_SEARCHES.filter((s) => s.businessId === businessId);
}

export function getSavedSearchesByUser(userId: string): SavedSearchSeedData[] {
  return SAVED_SEARCHES.filter((s) => s.userId === userId);
}

export function getSavedSearchesByEntity(entity: SavedSearchEntity): SavedSearchSeedData[] {
  return SAVED_SEARCHES.filter((s) => s.entity === entity);
}

export function getPinnedSavedSearches(): SavedSearchSeedData[] {
  return SAVED_SEARCHES.filter((s) => s.isPinned);
}

export function getSharedSavedSearches(businessId: string): SavedSearchSeedData[] {
  return SAVED_SEARCHES.filter(
    (s) => s.businessId === businessId && (s.visibility === 'team' || s.visibility === 'business'),
  );
}
